/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import RenderDocument from "App/render_document";
import View from "App/view/view";
import AnimatedObject from "App/animations/animated_object";
import ViewMode from "App/view/modes/view_mode";
import Camera from "App/view/cameras/camera";
import {mat4, quat, vec2, vec3, vec4} from "gl-matrix";
import BoundingBox from "App/utils/bounding_box";
import {degToRad, radToDeg} from "App/math";
import {logInfo} from "App/debug";
import {project, unProject} from "App/utils/glu";
import CameraAnimator from "App/animations/camera_animator";
import CameraWebVR from "App/view/cameras/camera_web_vr";
import CameraStereo from "App/view/cameras/camera_stereo";
import view_mode_web_vr from "App/view/modes/view_mode_web_vr";
import IScene from "js_interface/src/scene_interface";
import {MouseActionEnum, EViewControlsFamily, EViewControls} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

export enum ViewControlsPickDepthEnum {
    FROM_TOP = 0,
    FROM_BOTTOM = 1
}

export class MouseState {
    public  dx: number = 0;
    public  dy: number = 0;
    public  lastX: number = -1;
    public  lastY: number = -1;
    public  pressX: number = -1;
    public  pressY: number = -1;
    public  lastButton: number = 0;
    public  isMoving: boolean = false;

    public  set(aDx: number, aDy: number, aLastX: number, aLastY: number, aPressX: number, aPressY: number, aLastButton: number, aIsMoving: boolean) {
        this.dx = aDx;
        this.dy = aDy;
        this.lastX = aLastX;
        this.lastY = aLastY;
        this.pressX = aPressX;
        this.pressY = aPressY;
        this.lastButton = aLastButton;
        this.isMoving = aIsMoving;
    }
}

export default
class ViewControls extends AnimatedObject {

    public static VIEW_ANGLE_MIN = 45; // 0 ?
    public static VIEW_ANGLE_MAX = 90; // 180 ?

    protected _sceneAPI: IScene;
    protected _document: RenderDocument;
    protected _view: View;
    protected _viewMode: ViewMode | view_mode_web_vr;
    protected _camera: Camera | CameraWebVR | CameraStereo | null = null;
    protected _family: EViewControlsFamily;

    protected _lastSpeedTime: number = 0.0;
    protected _gamepadZero: number = 0.25; // gamepad API zero limit

    protected _allowLogDepth: boolean = false;
    protected _useLogDepth: boolean = false;

    protected _mouseState: MouseState = new MouseState();
    protected mouseSensitivity: number = 0;
    protected _autoMovementAllowed: boolean = false;
    protected _touchHandling: boolean = false; // needed to know rotate/move events were initiated by touch

    // special keys flags
    protected _shiftDown: boolean = false;
    protected _ctrlDown: boolean = false;

    protected CDWK_FP_MODE_NUM_STEPS: number = 0;	    // Number of steps (mouse wheel scrolls) to get to max speed.
    protected CDWK_FP_MODE_MIN_MAX_SPEED: number = 0;      // Minimal maximal speed (to be able to move fast even in small models)
    protected CDWK_FP_MODE_MAX_SPEED_RATIO: number = 0;      // Maximal speed ratio to compute max speed

    // controls movement codes
    protected goForward: number = 0;
    protected goBack: number = 1;
    protected goLeft: number = 2;
    protected goRight: number = 3;
    protected goUp: number = 4;
    protected goDown: number = 5;
    protected speedUp: number = 6;
    protected speedDown: number = 7;

    // cadwork gamepad controls definitions
    protected position1: number = 0;     // move position forward (1) / backward (-1)
    protected position2: number = 1;     // move position right (1) / left (-1)
    protected position3: number = 2;     // move position up (1) / down (-1)
    protected target1: number = 3;       // rotate target up (1) / down (-1)
    protected target2: number = 4;       // rotate target right (1) / left (-1)
    protected target3: number = 5;       // rotate target roll right (1) / roll left (-1)
    protected  moveSpeed: number = 6;     // movement speed value

    protected gamepadFlagMap: number[] = [];
    protected _pointerLockUsed: boolean = false;
    protected step: number = 0;
    protected _pgc: any = {
        mat4Inverted: mat4.create(),
        windowPoint: vec4.create(),
        pickedPoint: vec3.create(),
        tmpVec3: vec3.create(),
        worldQuat: quat.create()
    };

    protected openingAngle: number;
    protected _collisionCameraForward: Camera;
    protected _cumulYRot: number = 0.0;

    // gamepad api
    private _haveGamepadEvents: boolean = false;
    private _registeredGamepads: any = {};
    private _supportedGamepads: string[] = ["mocute", "xinput", "dinput", "oculus touch", "openvr gamepad",
                                            "oculus go controller"]; // all lower case, string representing part of uid
    private _gamepadCheck: number = null;
    private _currentGamepads: Gamepad[] = [];
    private gamepadconnectedCB: any = null;
    private gamepaddisconnectedCB: any = null;

    private _pointerLockAvailable: boolean;
    private _deviceInputInitialized: boolean = false;
    private pointerLockChangeCallback: any;

    // this is used to store the last movement{X|Y} from pointer lock event becasue
    // there is bug in chrome which behave so that we have sequence of positive values
    // followed by big (> 200) negative value ...
    private _pointerLock: any = {
        prevMovementX: 0,
        prevMovementY: 0
    };

    private _enabled: boolean = true;
    private _initYRotAngle: number = 0;

    private _keepViewDirection: boolean = false;

    constructor(aDocument: RenderDocument, aView: View) {
        super();

        this._sceneAPI = aDocument.getSceneAPI();
        this._document = aDocument;
        this._view = aView;
        this._viewMode = aView.getViewMode();

        this._haveGamepadEvents = 'ongamepadconnected' in window;

        this.gamepadFlagMap[this.position1] = 0;
        this.gamepadFlagMap[this.position2] = 0;
        this.gamepadFlagMap[this.position3] = 0;
        this.gamepadFlagMap[this.target1] = 0;
        this.gamepadFlagMap[this.target2] = 0;
        this.gamepadFlagMap[this.target3] = 0;
        this.gamepadFlagMap[this.moveSpeed] = 0;

        // pointer lock api
        this._pointerLockAvailable = this.detectPointerLock();

        this.mouseSensitivity = 3;

        this._family = EViewControlsFamily.ABSTARACT;
    }

    public getMouseState(): MouseState {
        return this._mouseState;
    }

    public enable(aValue: boolean) {
        this._enabled = aValue;
    }

    public isEnabled(): boolean {
        return this._enabled;
    }

    public getViewAngleLimits(): {min: number, max: number} {
        return {
            min: ViewControls.VIEW_ANGLE_MIN,
            max: ViewControls.VIEW_ANGLE_MAX
        };
    }

    public init() {
        this.initGamepads();
    }

    public restore() {
        this.init();
        this._initCameraParams();
    }

    // reset mode specialities
    public destroy() {
        //
    }

    // Set user defined mouse sensitivity. Limits <1, 5> checked.
    public setMouseSensitivity(aValue: number) {
        this.mouseSensitivity = aValue;
    }

    public checkAndInitLogarithmicDepthBuffer() {
        let recompileShaders = () => {
            let webglRenderGraph = this._document.getRenderer().getWebGLRenderGraph();
            if (webglRenderGraph) {
                webglRenderGraph.initializeShaders();
            }
        };

        // this is used in settings to define that we would like to use the logarithmic depth buffer
        if (this._testNeedLogarithmicDepthBuffer()) {
            let logDepthSupported = this._sceneAPI.renderSettingsModule.getLogarithmicDepthBufferSupportEnabled();
            if (!logDepthSupported) {
                this._sceneAPI.renderSettingsModule.set({ geometry: { logDepthBufferEnabled: true } });

                // we ask here because the set is not quaranteed not to succeeded because of the log dopth buffer
                // required gl extensions are not available
                if (this._sceneAPI.renderSettingsModule.getLogarithmicDepthBufferSupportEnabled()) {
                    // recompile shaders
                    recompileShaders();
                }
            }
        }
        else {
            // compile shaders without log depth buffer support
            let logDepthSupported = this._sceneAPI.renderSettingsModule.getLogarithmicDepthBufferSupportEnabled();
            if (logDepthSupported) {
                this._sceneAPI.renderSettingsModule.set({ geometry: { logDepthBufferEnabled: false } });
                // recompile shaders
                recompileShaders();
            }
        }
    }

    public setUseLogarithmicDepthBuffer(aValue: boolean) {
        this._useLogDepth = false;

        if (!this._allowLogDepth) {
            return;
        }

        if (!this._sceneAPI.renderSettingsModule.getLogarithmicDepthBufferSupportEnabled()) {
            return;
        }

        this._useLogDepth = aValue && this._testNeedLogarithmicDepthBuffer();
    }

    public getUseLogarithmicDepthBuffer(): boolean {
        return this._useLogDepth;
    }

    public getCamera(): Camera | CameraWebVR | CameraStereo | null {
        return this._camera;
    }

    public viewpointChanged() {
        this._camera._invalidatePerspectiveProjMatrix();
    }

    public onBoundingBoxUpdated(aBoundingBox: BoundingBox, aBoundingBoxForNPC: BoundingBox) {
        this._camera.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);
    }

    public getCumulRotation(): number {
        return this._cumulYRot;
    }

    public setCumulRotation(aValue: number) {
        this._cumulYRot = aValue;
    }

    public rotateCameraTarget(aRadX: number, aRadY: number) {
        let factor = this.mouseSensitivity;
        let wRad = -aRadX / (factor * 0.3);
        let hRad = -aRadY / (factor * 0.3); // note the opposite direction than in walking mode (more flying friendly)

        let rotmat = mat4.create();

        // rotate around Y
        let viewDir = this._camera.getCameraDirection();
        mat4.rotateY(rotmat, mat4.create(), wRad);
        vec3.transformMat4(viewDir, viewDir, rotmat);

        let newTarget = vec3.create();
        vec3.add(newTarget, this._camera.getCameraPosition(), viewDir);
        this._camera.setCameraTarget(newTarget);

        // rotate around X
        // first we need x axis in transformed CS
        let right = vec3.create();
        vec3.cross(right, this._camera.getCameraDirection(), this._camera.getCameraUpVectorRef());
        vec3.normalize(right, right);

        // create rot matrix which will rotate about the hRad radians
        mat4.rotate(rotmat, mat4.create(), hRad, right);

        viewDir = this._camera.getCameraDirection();
        vec3.transformMat4(viewDir, viewDir, rotmat);
        vec3.add(newTarget, this._camera.getCameraPosition(), viewDir);

        // limit angle between up vector and the viewdir
        let newViewDir = vec3.create();
        vec3.subtract(newViewDir, newTarget, this._camera.getCameraPosition());
        let dotproduct = vec3.dot(newViewDir, vec3.fromValues(0, 1, 0));
        let fi = dotproduct;
        // let angle = (fi * 180) / Math.PI;
        // logInfo(fi + ", " + angle);
        if (fi < -0.99 || fi > 0.99) {
            return;
        }

        this._camera.setCameraTarget(newTarget);
    }

    public restrictCameraToGround() {
        let points = this._pickDepth("cdwk_ground_depth", ViewControlsPickDepthEnum.FROM_BOTTOM);

        let cameraPosition = this._camera.getCameraPosition();
        let documentBBox = this._document.getBoundingBox();

        if (points[0]) {
            let restrictedEye = vec3.clone(points[0]);
            // make the camera smoother when moving on no flat terrain
            restrictedEye[1] = Math.floor(restrictedEye[1]) + this._camera.getNearValue() + (this._camera.getNearValue() / 10.0);

            // for camera bellow terrain
            if (cameraPosition[1] < restrictedEye[1]) {
                this._camera.setCameraPosition(vec3.fromValues(cameraPosition[0], restrictedEye[1], cameraPosition[2]));
            }
        }

        // restrict camera to be above bottom of the scene bounding box
        if (this._sceneAPI.renderSettingsModule.getKeepCameraAboveSceneBBoxBottomPlane() && (cameraPosition[1] < documentBBox.getMin()[1])) {
            this._camera.setCameraPosition(vec3.fromValues(cameraPosition[0], documentBBox.getMin()[1], cameraPosition[2]));
        }
    }

    public isCameraAboveGeometry(): boolean {
        // prepare the matrix for ground picking from top of the scene
        this._camera.backup();

        // set current view to look down from camera position
        let camPos = this._camera.getCameraPosition();
        this._camera.setCameraPositionAndTarget(
            camPos,
            vec3.fromValues(camPos[0], camPos[1] - 10.0, camPos[2])
        );

        // do not use up vector [0,1,0] when camera is looking down, because the view vector direction 0,-1,0 causes that the model view
        // matrix is computed strangely
        this._camera.setCameraUpVector(
            vec3.fromValues(0, 0, 1)
        );

        this._camera.forceNearValue(0.001); // we just keep the near plane as close as possible as we just need to know if we pick something or not

        let centerWindowPoint = this._view.getCenterWindowPoint();
        let points = this._document.renderForValues(this._view, {
            type: "cdwk_depth",
            readPosition: [
                centerWindowPoint[0],
                centerWindowPoint[1]
            ]
        });

        this._camera.restore();
        return points[0] !== null;
    }

    /**
     * Returns view controls family type (FLY/WALK/EXAMINER...)
     */
    public getControlsTypeFamily(): EViewControlsFamily {
        return this._family;
    }

    /**
     * Returns exact view controls type.
     */
    public getControlsType(): EViewControls {
        return EViewControls.ABSTARACT;
    }

    public setEnableDeviceInput(aValue: boolean, aKeepViewDirection: boolean) {
        super.setEnableDeviceInput(aValue, aKeepViewDirection);

        if (!aValue) {
            let mainModel = this._document.getMainModel();
            let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;
            if (zeropoint) {
                let zeropointRotation = zeropoint.getRotation();
                zeropoint.setRotation(zeropointRotation - this._initYRotAngle);
            }
        }

        this._deviceInputInitialized = false;
        this._keepViewDirection = aKeepViewDirection === undefined ? true : aKeepViewDirection;
        this._initYRotAngle = 0;
    }

    public getView(): View {
        return this._view;
    }

    public render() {
        //
    }

    public tick(): void {
        this.checkGamepad();

        if (!this._deviceInputEnabled) {
            return;
        }

        let motionData = this._sceneAPI.getCanvas().getMotionData();
        if (motionData.changed && motionData.deviceOrientation) {

            let cameraQuat = this._computeCameraQuaternionFromDeviceMotionData();

            if (!cameraQuat) { // when sensors are disabled (usually when debugging on desktop)
                return;
            }

            let viewDir = vec3.create();
            vec3.transformQuat(viewDir, vec3.fromValues(0, 0, -1), cameraQuat);

            let yAngle = Math.atan2(viewDir[2], viewDir[0]) - Math.atan2(-1, 0);

            if (!this._deviceInputInitialized) {
                let currDir = this._camera.getCameraDirection();

                this._initYRotAngle = yAngle;
                this._initYRotAngle -= Math.atan2(currDir[2], currDir[0]) - Math.atan2(-1, 0);
                if (this._initYRotAngle < 0) {
                    this._initYRotAngle += 2 * Math.PI;
                }

                // when we would like to have the direction initialized from device's compass
                if (!this._keepViewDirection) {
                    this._initYRotAngle = 0;
                }

                // offset the compass with the initYRotAngle value
                let mainModel = this._document.getMainModel();
                let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;
                if (zeropoint) {
                    let zeropointRotation = zeropoint.getRotation();
                    zeropoint.setRotation(zeropointRotation + this._initYRotAngle);
                }

                this._deviceInputInitialized = true;
            }

            if (yAngle < 0) {
                yAngle += 2 * Math.PI;
            }

            let correctedViewDir = vec3.create();
            let yrotQuat = quat.create();
            quat.rotateY(yrotQuat, yrotQuat, this._initYRotAngle - yAngle);

            vec3.transformQuat(correctedViewDir, vec3.fromValues(0, 0, -1), yrotQuat);
            correctedViewDir[1] = viewDir[1];

            viewDir = correctedViewDir;

            let target = vec3.create();
            vec3.add(target, this._camera.getCameraPosition(), viewDir);

            // holy hack
            let orient = motionData.screenOrientation || 0;
            let upvec = vec3.fromValues(0.0001, orient < 0.1 ? -1 : 1, 0.0001);

            let initRotQuat = quat.create();
            quat.rotateY(initRotQuat, initRotQuat, this._initYRotAngle);
            quat.multiply(cameraQuat, initRotQuat, cameraQuat);
            vec3.transformQuat(upvec, upvec, cameraQuat);

            this._camera.setCameraUpVector(upvec);
            this._camera.setCameraTarget(target);
            this._view.viewpointChanged();
        }
    }

    /**
     * Returns moving state of view
     *
     * @return {Boolean} moving state of view area
     */
    public isMoving(): boolean {
        return this._mouseState.isMoving;
    }

    public getLastMousePosition(): vec2 {
        return vec2.fromValues(this._mouseState.lastX, this._mouseState.lastY);
    }

    public getUIDFromGamepad(aGamepad: Gamepad): string {
        return aGamepad.id + (aGamepad.hand ? aGamepad.hand : "");
    }

    public scanGamepads() {
        //@ts-ignore
        let gamepads = navigator.getGamepads !== undefined ? navigator.getGamepads() : (navigator.webkitGetGamepads !== undefined ? navigator.webkitGetGamepads() : []);
        if (gamepads.length === this._currentGamepads.length) {
            return;
        }

        for (let gamepad of gamepads) {
            if (gamepad) {
                if (!(this.getUIDFromGamepad(gamepad) in this._registeredGamepads)) {
                    this.registerGamepad(gamepad);
                }
            }
        }

        this._currentGamepads = gamepads;

        this._viewMode.scheduleRedraw();
    }

    public updateGamepads() {
        //@ts-ignore
        let gamepads = navigator.getGamepads !== undefined ? navigator.getGamepads() : (navigator.webkitGetGamepads !== undefined ? navigator.webkitGetGamepads() : []);
        for (let gamepad of gamepads) {
            if (gamepad) {
                // console.log("update gamepad " + gamepads[i].axes[0] + ", " + gamepads[i].axes[1] + ", " + gamepads[i].id);
                this.updateGamepad(gamepad);
            }
        }
    }

    public initGamepads() {
        if (this.gamepaddisconnectedCB == null) {
            this.gamepaddisconnectedCB = (e: GamepadEvent) => {
                this.registerGamepad(e.gamepad);
            };
        }
        else {
            window.removeEventListener("gamepaddisconnected", this.gamepaddisconnectedCB);
        }

        if (this.gamepaddisconnectedCB == null) {
            this.gamepaddisconnectedCB = (e: GamepadEvent) => {
                this.registerGamepad(e.gamepad);
            };
        }
        else {
            window.removeEventListener("gamepadconnected", this.gamepadconnectedCB);
        }

        window.addEventListener("gamepadconnected", this.gamepadconnectedCB);
        window.addEventListener("gamepaddisconnected", this.gamepaddisconnectedCB);

        if (!this._haveGamepadEvents) {
            // setup an interval for Chrome
            if (!this._gamepadCheck) {
                this._gamepadCheck = window.setInterval(this.scanGamepads.bind(this), 1000);
            }
        }
    }

    public logGamepad(): string {
        let gp = navigator.getGamepads()[0];
        let html = "";
        if (gp) {
            html += "id: " + gp.id + "\n";

            for (let i = 0; i < gp.buttons.length; i++) {
                html += "Button " + (i + 1) + ": ";
                if (gp.buttons[i].pressed) {
                    html += " pressed";
                }
                html += "\n";
            }

            for (let i = 0; i < gp.axes.length; i += 2) {
                html += "Stick " + (Math.ceil(i / 2) + 1) + ": " + gp.axes[i] + "," + gp.axes[i + 1] + "\n";
            }

            logInfo(html);
        }
        return html;
    }

    /**
     *  Try to register stored gamepad profile.
     */
    public updateGamepad(aGamepad: Gamepad) {
        if (!aGamepad || aGamepad.id === undefined) {
            return;
        }

        this._registeredGamepads[this.getUIDFromGamepad(aGamepad)] = aGamepad;
    }

    public registerGamepad(aGamepad: Gamepad) {
        this._registeredGamepads[this.getUIDFromGamepad(aGamepad)] = aGamepad;

        logInfo('Registering gamepad ' + this.getUIDFromGamepad(aGamepad));
    }

    public unregisterGamepad(aGamepad: Gamepad) {
        if (!aGamepad || aGamepad.id === undefined) {
            return;
        }

        if (this._registeredGamepads[this.getUIDFromGamepad(aGamepad)] !== undefined) {
            delete this._registeredGamepads[this.getUIDFromGamepad(aGamepad)];

            logInfo('Unregistering gamepad ' + this.getUIDFromGamepad(aGamepad));
        }

        if (Object.keys(this._registeredGamepads).length === 0) {
            if (this._gamepadCheck) {
                clearInterval(this._gamepadCheck);
                this._gamepadCheck = null;
            }
        }
    }

    /***
     * Return first form supported and connected gamepads
     * @returns {*}
     */
    public bindGamepadButtons(): boolean {
        let hasSomehing = false;
        for (let key in this._registeredGamepads) {
            let gamepad = this._registeredGamepads[key];
            for (let supportedGamepad of this._supportedGamepads) {
                if (gamepad.id.toLowerCase().indexOf(supportedGamepad) !== -1) {
                    switch (supportedGamepad) {
                        case "xinput":
                        case "dinput":
                            this.checkGamepadXbox360(gamepad);
                            hasSomehing = true;
                            break;
                        case "mocute":
                            this.checkGamepadMocute(gamepad);
                            hasSomehing = true;
                            break;
                        case "oculus touch":
                            if (gamepad.id.toLowerCase().indexOf("left") !== -1) {
                                this.checkGamepadOculusTouchLeft(gamepad);
                            } else if (gamepad.id.toLowerCase().indexOf("right") !== -1) {
                                this.checkGamepadOculusTouchRight(gamepad);
                            }
                            hasSomehing = true;
                            break;
                        case "openvr gamepad":
                            if (gamepad.hand.toLowerCase().indexOf("left") !== -1) {
                                this.checkGamepadHTCTouchLeft(gamepad);
                            } else if (gamepad.hand.toLowerCase().indexOf("right") !== -1) {
                                this.checkGamepadHTCTouchRight(gamepad);
                            }
                            hasSomehing = true;
                            break;
                        case "oculus go controller":
                            this.checkGamepadOculusGoController(gamepad);
                            hasSomehing = true;
                            break;
                    }
                }
            }
        }

        return hasSomehing;
    }

    /***
     *
     */
    public checkGamepad() {
        if (!this._haveGamepadEvents) {
            this.updateGamepads();
        }

        if (this.bindGamepadButtons()) {
            this._processGamepads();
        }
    }

    public writeGamepadButtons(aGamepad: Gamepad) {
        let html = "";
        for (let i = 0; i < aGamepad.buttons.length; i++) {
            // html += "Button " + (i + 1) + ": ";
            if (aGamepad.buttons[i].pressed) {
                html += i + " pressed, ";
            }
        }
        logInfo(html);
    }

    public checkGamepadMocute(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.goForward] = aGamepad.buttons[12].value; // pressed
        this.gamepadFlagMap[this.goBack] = aGamepad.buttons[13].value;
        this.gamepadFlagMap[this.goLeft] = aGamepad.buttons[14].value;
        this.gamepadFlagMap[this.goRight] = aGamepad.buttons[15].value;
    }

    // Gamepad detection: http://html5gamepad.com/
    public checkGamepadXbox360(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.position1] = -aGamepad.axes[1];
        this.gamepadFlagMap[this.position2] = aGamepad.axes[0];
        this.gamepadFlagMap[this.target1] = aGamepad.axes[3];
        this.gamepadFlagMap[this.target2] = aGamepad.axes[2];
        this.gamepadFlagMap[this.moveSpeed] = aGamepad.buttons[7].value - aGamepad.buttons[6].value;

        if (Math.abs(aGamepad.axes[0]) > 0.1 || Math.abs(aGamepad.axes[1]) > 0.1 ||
            Math.abs(aGamepad.axes[2]) > 0.1 || Math.abs(aGamepad.axes[3]) > 0.1 ||
            this.gamepadFlagMap[this.moveSpeed] !== 0) {
            this._viewMode.scheduleRedraw();
        }
    }

    public checkGamepadOculusTouchLeft(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.position1] = -aGamepad.axes[1];
        this.gamepadFlagMap[this.position2] = aGamepad.axes[0];
        this.gamepadFlagMap[this.moveSpeed] = aGamepad.buttons[1].pressed ? -1 : 0;

        if (Math.abs(aGamepad.axes[0]) > 0.00001 || Math.abs(aGamepad.axes[1]) > 0.00001 || this.gamepadFlagMap[this.moveSpeed] !== 0) {
            this._viewMode.scheduleRedraw();
        }
    }

    public checkGamepadOculusTouchRight(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.target1] = aGamepad.axes[1];
        this.gamepadFlagMap[this.target2] = aGamepad.axes[0];
        this.gamepadFlagMap[this.moveSpeed] = aGamepad.buttons[1].pressed ? 1 : this.gamepadFlagMap[this.moveSpeed];

        if (Math.abs(aGamepad.axes[0]) > 0.00001 || Math.abs(aGamepad.axes[1]) > 0.00001 || this.gamepadFlagMap[this.moveSpeed] !== 0) {
            this._viewMode.scheduleRedraw();
        }
    }

    public checkGamepadHTCTouchLeft(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.position1] = aGamepad.axes[1];
        this.gamepadFlagMap[this.position2] = aGamepad.axes[0];
        this.gamepadFlagMap[this.moveSpeed] = aGamepad.buttons[1].pressed ? -1 : 0;

        if (Math.abs(aGamepad.axes[0]) > 0.00001 || Math.abs(aGamepad.axes[1]) > 0.00001 || this.gamepadFlagMap[this.moveSpeed] !== 0) {
            this._viewMode.scheduleRedraw();
        }
    }

    public checkGamepadHTCTouchRight(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.target1] = -aGamepad.axes[1];
        this.gamepadFlagMap[this.target2] = aGamepad.axes[0];
        this.gamepadFlagMap[this.moveSpeed] = aGamepad.buttons[1].pressed ? 1 : this.gamepadFlagMap[this.moveSpeed];

        if (Math.abs(aGamepad.axes[0]) > 0.00001 || Math.abs(aGamepad.axes[1]) > 0.00001 || this.gamepadFlagMap[this.moveSpeed] !== 0) {
            this._viewMode.scheduleRedraw();
        }
    }

    public checkGamepadOculusGoController(aGamepad: Gamepad) {
        this.gamepadFlagMap[this.position1] = -aGamepad.axes[1];
        this.gamepadFlagMap[this.position2] = aGamepad.axes[0];
        this.gamepadFlagMap[this.moveSpeed] = aGamepad.buttons[0].pressed ? -1 : (aGamepad.buttons[1].pressed ? 1 : 0);

        if (Math.abs(aGamepad.axes[0]) > 0.00001 || Math.abs(aGamepad.axes[1]) > 0.00001 || this.gamepadFlagMap[this.moveSpeed] !== 0) {
            this._viewMode.scheduleRedraw();
        }
    }

    public detectPointerLock(): boolean {
        return 'pointerLockElement' in document ||
            'mozPointerLockElement' in document ||
            'webkitPointerLockElement' in document;
    }

    public setUsePointerLock(): boolean {
        if (this._pointerLockAvailable) {
            this.setupPointerLock();
            return true;
        } else {
            return false;
        }
    }

    public setupPointerLock() {

        let onMovePointerLockCb = (e: any) => {
            this.onMovePointerLock(e);
        };

        // called when the pointer lock has changed. Here we check whether the
        // pointerlock was initiated on the element we want.
        this.pointerLockChangeCallback = () => {
            if (!this._pointerLockUsed) {
                // we've got a pointerlock for our element, add a mouselistener
                this._view.getHTMLCanvas().addEventListener("mousemove", onMovePointerLockCb, false);
                this._pointerLockUsed = true;
            } else {
                // pointer lock is no longer active, remove the callback
                this._view.getHTMLCanvas().removeEventListener("mousemove", onMovePointerLockCb, false);
                document.removeEventListener('pointerlockchange', this.pointerLockChangeCallback, false);
                this._pointerLockUsed = false;
            }
        };

        // register the callback when a pointerlock event occurs
        document.addEventListener('pointerlockchange', this.pointerLockChangeCallback, false);

        let canvas = this._view.getHTMLCanvas();
        //@ts-ignore
        canvas.requestPointerLock = canvas.requestPointerLock || canvas.mozRequestPointerLock || canvas.webkitRequestPointerLock;

        // Ask the browser to lock the pointer)
        //@ts-ignore
        canvas.requestPointerLock();
    }

    public setCameraPositionAndTarget(aCameraPosition: vec3, aCameraTarget: vec3) {
        this._camera.setCameraPositionAndTarget(aCameraPosition, aCameraTarget);
    }

    // Re-set camera target with new pick
    // (during FPS mode is too close to eye that does not feel like orbit camera)
    public pickAndSetCameraTarget() {
        // re-set camera target (during walk mode is too close to eye that does not feel like orbit camera)

        // pick at view center
        let centerWindowPoint = this._view.getCenterWindowPoint();
        let points = this._document.renderForValues(this._view, {
            type: "cdwk_depth",
            readPosition: [
                centerWindowPoint[0],
                centerWindowPoint[1]
            ]
        });

        if (!points[0]) {
            return;
        }

        let position = this._view.getPosition();
        let size = this._view.getSize();
        project(
            this._pgc.windowPoint,
            points[0],
            this._camera.getViewProjectionMatrix(),
            [position[0], position[1], size[0], size[1]]
        );

        // here we need as an input the center of the view in 2d coordinate system defined by the canvas
        unProject(
            this._pgc.pickedPoint,
            centerWindowPoint[0],
            centerWindowPoint[1],
            this._pgc.windowPoint[2],
            this._camera.getViewProjectionMatrix(),
            [position[0], position[1], size[0], size[1]]
        );

        this._camera.setCameraTarget(this._pgc.pickedPoint);
    }

    /**
     *  Mouse buttons:
     *  1: left
     *  2: right
     *  3:
     *  4: middle
     *  5:
     *  6:
     *  7:
     *  8: wheel
     */

    // default values
    public getRotateButtons(): number[] {
        let lmbFunc = this._sceneAPI.viewControlsModule.getLeftMouseButtonFunctionality();
        let mmbFunc = this._sceneAPI.viewControlsModule.getMiddleMouseButtonFunctionality();
        let rmbFunc = this._sceneAPI.viewControlsModule.getRightMouseButtonFunctionality();

        let buttons = [];
        if (lmbFunc === MouseActionEnum.ROTATE || this._touchHandling) {
            buttons.push(1);
        }

        if (mmbFunc === MouseActionEnum.ROTATE) {
            buttons.push(4);
        }

        if (rmbFunc === MouseActionEnum.ROTATE) {
            buttons.push(2);
        }

        return buttons;
    }

    public isRotating(buttonState: number): boolean {
        let rotateButtons = this.getRotateButtons();

        let ret = false;
        for (let button of rotateButtons) {
            ret = ret || ((buttonState & button) === button);
        }

        return ret;
    }

    // default values
    public getPanButtons(): number[] {
        let lmbFunc = this._sceneAPI.viewControlsModule.getLeftMouseButtonFunctionality();
        let mmbFunc = this._sceneAPI.viewControlsModule.getMiddleMouseButtonFunctionality();
        let rmbFunc = this._sceneAPI.viewControlsModule.getRightMouseButtonFunctionality();

        let buttons = [];
        if (lmbFunc === MouseActionEnum.PAN) {
            buttons.push(1);
        }

        if (mmbFunc === MouseActionEnum.PAN) {
            buttons.push(4);
        }

        if (rmbFunc === MouseActionEnum.PAN) {
            buttons.push(2);
        }

        if (buttons.length === 0) {
            buttons.push(4); // default value when pan func is not set
        }

        return buttons;
    }

    // default values
    public getWheelButton(): number {
        return 8;
    }

    public onMovePointerLock(e: any) {
        let dx = e.movementX || 0;
        let dy = e.movementY || 0;

        if ((this._pointerLock.prevMovementX >= 0 && dx < 0 && Math.abs(dx) > 200) ||
            (this._pointerLock.prevMovementX <= 0 && dx > 0 && Math.abs(dx) > 200)) {
            dx = 0;
            if (this._pointerLock.prevMovementX !== 0) {
                dx = this._pointerLock.prevMovementX / this._pointerLock.prevMovementX; // just store sign
            }
        }

        if ((this._pointerLock.prevMovementY >= 0 && dy < 0 && Math.abs(dy) > 200) ||
            (this._pointerLock.prevMovementY <= 0 && dy > 0 && Math.abs(dy) > 200)) {
            dy = 0;
            if (this._pointerLock.prevMovementY !== 0) {
                dy = this._pointerLock.prevMovementY / this._pointerLock.prevMovementY; // just store sign
            }
        }

        this._pointerLock.prevMovementX = dx;
        this._pointerLock.prevMovementy = dy;

        // propagate the different to the event
        e.dx = dx;
        e.dy = dy;
    }

    public onMousePress(x: number, y: number, buttonState: number): boolean {
        return false;
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number): boolean {
        return false;
    }

    public onMove(x: number, y: number, buttonState: number): boolean {
        return false;
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number): boolean {
        return false;
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number): boolean {
        return false;
    }

    public onMouseIn(x: number, y: number, buttonState: number): boolean {
        return false;
    }

    public onMouseOut(x: number, y: number, buttonState: number): boolean {
        return false;
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean): boolean {
        return false;
    }

    public onDoubleClick(x: number, y: number): boolean {
        return false;
    }

    public onKeyDown(key: string) {
        //
    }

    public onKeyUp(key: string) {
        //
    }

    public onKeyPress(key: string) {
        //
    }

    public onFocus() {
        // this fixes the case when the on key up was leaked because of switching focus to another window
        this._shiftDown = false;
        this._ctrlDown = false;
    }

    public onTouchStart(x: number, y: number, numTouches: number): boolean {

        this._touchHandling = true;

        // solve default behavior
        switch (numTouches) {
            case 1:
                return this.onMousePress(x, y, 1); // left button
                break;
            case 2:
                return this.onMousePress(x, y, 4); // middle button
                break;
        }

        return false;
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number): boolean {

        this._touchHandling = true;

        // solve default behavior
        switch (numTouches) {
            case 1:
                return this.onDrag(x, y, 0, 1);
                break;
            case 2:
                let ret = this.onDrag(x, y, 0, 4);
                return this.onWheel(x, y, delta, 8) || ret;
                break;
        }

        return false;
    }

    public onTouchEnd(x: number, y: number, remainingTouch: any, aDraggedDuringTouch: boolean): boolean {

        // solve default behavior
        if (remainingTouch) {
            // we still press one finger so we finish the pan and restart the rotation
            return this.onMouseRelease(x, y, 0, 4);
        } else {
            this._touchHandling = false;
            return this.onMouseRelease(x, y, 0, 1);
        }

        return false;
    }

    public onSingleTap(x: number, y: number): boolean {
        return this.onMousePress(x, y, 1);
    }

    public onDoubleTap(x: number, y: number): boolean {
        return false;
    }

    public animated(aAnimatorObject: CameraAnimator, aTimeFactor: number) {
        let interpolator = aAnimatorObject.getInterpolator();

        this.setCameraPositionAndTarget(
            interpolator.getPosition(),
            interpolator.getTarget()
        );

        this._view.viewpointChanged();
    }

    public emitSpeed() {
        //
    }

    // Set user defined view angle in degrees. Limits <45, 90> checked.
    public setViewAngle(aViewAngle: number) {
        this.openingAngle = Math.max(ViewControls.VIEW_ANGLE_MIN, Math.min(aViewAngle, ViewControls.VIEW_ANGLE_MAX));
        this._camera.setFieldOfView(degToRad(this.openingAngle));

        if (this._collisionCameraForward) {
            this._collisionCameraForward.setFieldOfView(degToRad(this.openingAngle));
        }

        this._viewMode.scheduleRedraw();
    }

    // Get camera view angle in degrees
    public getViewAngle(): number {
        let fov = this._camera.getFieldOfView();

        return radToDeg(fov);
    }

    public rotate(aRadX: number, aRadY: number) {
        //
    }

    protected _initCameraParams() {
        // update bounding box for camera
        this._camera.onBoundingBoxUpdated(this._document.getBoundingBox(), this._document.getBoundingBoxForNPC());

        // update aspect ratio
        this._camera.setAspectRatio(this._view.getAspectRatio());
    }

    private _testNeedLogarithmicDepthBuffer(): boolean {
        // when scene bbox radius is bigger than 1km, then we will compile the shaders with log depth supported
        // (it affect the depth buffer precision so some artefacts appear to be more visible)
        let documentBBox = this._document.getBoundingBox();
        if (documentBBox.isValid()) {
            if (documentBBox.getDiameter() > 5000) {
                return true;
            }
        }

        return false;
    }

    /**
     *
     * @param aGeometryType
     * @param {TOP | DOWN} aFromTop
     * @returns {*}
     * @private
     */
    private _pickDepth(aGeometryType: string, aFromEnum: ViewControlsPickDepthEnum): vec3[] {
        // prepare the matrix for ground picking from top of the scene
        this._camera.backup();
        let min = vec3.clone(this._document.getBoundingBox().getMin());
        let max = vec3.clone(this._document.getBoundingBox().getMax());

        // set current view to look down
        let camPos = this._camera.getCameraPosition();

        // We cannot use the max[1] value to move the camera to the top looking down on the ground
        // as the precision is too small in the depth buffer so that we cannot go enough close to the ground on iPhone6.
        // So we set the near plane to 100.0 value and offset the camera position on the top of the scene bounding box
        // by this value too. This will increase the precision on the far plane.
        let nearValueOffset = 100.0;
        let gcCamFov = 0.0001;

        switch (aFromEnum) {
            case ViewControlsPickDepthEnum.FROM_TOP:
                this._camera.setCameraPositionAndTarget(
                    vec3.fromValues(camPos[0], max[1] + nearValueOffset, camPos[2]),
                    vec3.fromValues(camPos[0], min[1], camPos[2])
                );
                break;
            case ViewControlsPickDepthEnum.FROM_BOTTOM:
                this._camera.setCameraPositionAndTarget(
                    vec3.fromValues(camPos[0], min[1] - nearValueOffset, camPos[2]),
                    vec3.fromValues(camPos[0], max[1], camPos[2])
                );
                break;
        }

        this._camera.setCameraUpVector(
            vec3.fromValues(0, 0, 1) // do not use up vector [0,1,0] when camera is looking down,
                                              // because the view vector direction 0,-1,0 causes that the model
                                              // view matrix is computed strangely
        );
        this._camera.setFieldOfView(gcCamFov);
        this._camera.forceNearValue(nearValueOffset);

        // use the ground collision camera to perform the pick via this controls
        let centerWindowPoint = this._view.getCenterWindowPoint();
        let points = this._document.renderForValues(this._view, {
            type: aGeometryType, // "cdwk_ground_depth",
            readPosition: [
                centerWindowPoint[0],
                centerWindowPoint[1]
            ]
        });

        this._camera.restore();

        return points;
    }

    private _processGamepads() {
        // walking / flying speed
        if (Math.abs(this.gamepadFlagMap[this.moveSpeed]) === 1) {
            let currentSpeedTime = new Date().getTime();
            let delta = currentSpeedTime - this._lastSpeedTime;

            if (delta > 100) { // button press repeat time
                if (this.gamepadFlagMap[this.moveSpeed] > 0) {
                    if (this.step < this.CDWK_FP_MODE_NUM_STEPS - 1) {
                        this.step += 1;
                    }
                } else {
                    if (this.step > 0) {
                        this.step -= 1;
                    }
                }
                this.emitSpeed();
                this._lastSpeedTime = currentSpeedTime;
            }
        }

        let h = 0;
        let v = 0;
        let maxGamepadRotationPixels = 10;

        // camera rotation
        let target1 = this.gamepadFlagMap[this.target1];
        if (Math.abs(target1) > this._gamepadZero) {
            let rotTargetValue = maxGamepadRotationPixels * target1;
            v = this._view.verticalPixelsToRad(rotTargetValue);
        }

        let target2 = this.gamepadFlagMap[this.target2];
        if (Math.abs(target2) > this._gamepadZero) {
            let rotTargetValue = maxGamepadRotationPixels * target2;
            h = this._view.horizontalPixelsToRad(rotTargetValue);
        }

        if (h !== 0 || v !== 0) {
            this.rotateCameraTarget(h, v);
            this._view.viewpointChanged();
        }
    }

    private _computeCameraQuaternionFromDeviceMotionData(): quat {
        let motionData = this._sceneAPI.getCanvas().getMotionData();

        let alpha = degToRad(motionData.deviceOrientation.alpha); // Z
        let beta = degToRad(motionData.deviceOrientation.beta);  // X'
        let gamma = degToRad(motionData.deviceOrientation.gamma); // Y''

        let orient = motionData.screenOrientation || 0;

        // logInfo("alpha: " + alpha + ", beta: " + beta + ", gamma: " + gamma + ", orient: " + orient);

        if (!alpha && !beta && !gamma && !orient) { // check if we receive something - fixes wrong camera orientation on a desktop
            return null;
        }

        quat.setAxisAngle(this._pgc.worldQuat, vec3.fromValues(1, 0, 0), -Math.PI / 2); // - PI/2 around the x-axis

        // 'ZXY' for the device, but 'YXZ' for us
        let euler = vec3.fromValues(beta, alpha, -gamma);

        let c1 = Math.cos(euler[0] / 2);
        let c2 = Math.cos(euler[1] / 2);
        let c3 = Math.cos(euler[2] / 2);
        let s1 = Math.sin(euler[0] / 2);
        let s2 = Math.sin(euler[1] / 2);
        let s3 = Math.sin(euler[2] / 2);

        let x = s1 * c2 * c3 + c1 * s2 * s3;
        let y = c1 * s2 * c3 - s1 * c2 * s3;
        let z = c1 * c2 * s3 - s1 * s2 * c3;
        let w = c1 * c2 * c3 + s1 * s2 * s3;

        // orient the device
        let cameraQuat = quat.fromValues(z, y, -x, w);

        let tempQuat = quat.create();
        quat.setAxisAngle(tempQuat, vec3.fromValues(0, 1, 0), motionData.initialScreenOffset || 0);

        quat.multiply(cameraQuat, tempQuat, cameraQuat);
        quat.multiply(cameraQuat, cameraQuat, this._pgc.worldQuat);

        return cameraQuat;
    }

}
